// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import clsx from "clsx";
import {
  Checkbox,
  Divider,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Switch,
} from "antd";
import { AxiosResponse } from "axios";
import { toast } from "react-hot-toast";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";

// Local Imports
import Label from "../../../../components/label/label.component";
import CustomInput from "../../../../components/custom-input/custom-input.component";
import { LOADING } from "../../../../store/actions";
import instance from "../../../../store/axios";
import CorrectImage from "../../../../assets/correct.svg";

// Style Imports
import "./add-edit-user.scss";
import { encryptPassword } from "src/utils/helper/functions";

interface EditCustomerProps {
  onCancel: () => void;
  onConfirm: () => void;
  edit?: boolean;
  selectedUser?: any;
  updateProfile?: boolean;
}

const CheckboxGroup = Checkbox.Group;

const { Option } = Select;

const AddEditUser: React.FC<EditCustomerProps> = ({
  onCancel,
  onConfirm,
  edit = false,
  selectedUser,
  updateProfile = false,
}: EditCustomerProps): ReactElement => {
  const dispatch = useDispatch();
  const [submitLoginForm, setSubmitLoginForm] = useState(false);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [rolesData, setRolesData] = useState<string[]>([]);
  const [searchedRole, setSearchedRole] = useState<string[]>([]);
  const [isActive, setIsActive] = useState<boolean>(
    selectedUser?.isActive ?? true
  );
  const [passwordRoleOne, setPasswordRoleOne] = useState<boolean>(false);
  const [passwordRoleTwo, setPasswordRoleTwo] = useState<boolean>(false);
  const [passwordRoleThree, setPasswordRoleThree] = useState<boolean>(false);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitLoginForm(true);
      },
      () => {
        setSubmitLoginForm(false);
      }
    );
  }, [values]);

  useEffect(() => {
    getRolesData();
  }, []);

  const getRolesData = async (): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(`/${process.env.REACT_APP_ENDPOINT_VERSION}/role`);
      setRolesData(
        response.data?.data?.map((role: any) => {
          return { label: role.role, value: role.id };
        })
      );
      setSearchedRole(
        response.data?.data?.map((role: any) => {
          return { label: role.role, value: role.id };
        })
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem("star_prev_url", `/backend-users`);
    }

    dispatch({ type: LOADING, payload: false });
  };

  const filterRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    const roles = JSON.parse(JSON.stringify(rolesData));
    setSearchedRole([
      ...roles.filter((role: any) =>
        role?.label?.toLowerCase().includes(event.target.value.toLowerCase())
      ),
      ...roles.filter(
        (role: any) =>
          !role?.label?.toLowerCase().includes(event.target.value.toLowerCase())
      ),
    ]);
  };

  const onSubmit = async (values: any) => {
    values.isActive = isActive ? true : false;

    dispatch({ type: LOADING, payload: true });

    values.password = encryptPassword(values.password);
    if (selectedUser) {
      if (!values.password) {
        delete values.password;
      }
      if (updateProfile) {
        delete values.roles;
      }
      try {
        await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/admin/${selectedUser?.id}`, values);
        toast.success("User updated successfully");
        onConfirm();
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
        localStorage.setItem("star_prev_url", `/backend-users`);
      }
    } else {
      try {
        await instance.post(`/${process.env.REACT_APP_ENDPOINT_VERSION}/admin`, values);
        toast.success("User added successfully");
        onConfirm();
        window.location.reload();
      } catch (error: any) {
        if (error?.response?.status !== 401) {
          toast.error(error?.response?.data?.message || "An error occurred");
        }
        localStorage.setItem("star_prev_url", `/backend-users`);
      }
    }

    dispatch({ type: LOADING, payload: false });
  };

  const onPasswordChange = (event: any) => {
    const lowerCaseRegex = /[a-z]/;
    const upperCaseRegex = /[A-Z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

    if (event.target.value?.length >= 8) {
      setPasswordRoleOne(true);
    } else {
      setPasswordRoleOne(false);
    }

    if (
      lowerCaseRegex.test(event.target.value) &&
      upperCaseRegex.test(event.target.value)
    ) {
      setPasswordRoleTwo(true);
    } else {
      setPasswordRoleTwo(false);
    }

    if (
      numberRegex.test(event.target.value) &&
      specialCharRegex.test(event.target.value)
    ) {
      setPasswordRoleThree(true);
    } else {
      setPasswordRoleThree(false);
    }
  };

  return (
    <div className="add-edit-user-container flex flex-col w-full px-5 md:px-10 box-border">
      <h3 className=" text-lg md:text-2xl font-semibold m-0">
        {edit
          ? updateProfile
            ? `Edit profile: ${selectedUser?.name}`
            : `Edit user: ${selectedUser?.name}`
          : "Create a new user"}
      </h3>
      {!edit && (
        <p className=" text-blue-200 text-base font-medium">
          Please fill out the following fields to create a new user
        </p>
      )}
      <Form
        form={form}
        onFinish={onSubmit}
        scrollToFirstError={true}
        initialValues={{
          name: selectedUser?.name,
          email: selectedUser?.email,
          roles: selectedUser?.roles?.map((role: any) => role.id),
          isActive: selectedUser?.isActive ? true : false,
          kycReviewGroup: selectedUser?.kycReviewGroup,
          withdrawGroup: selectedUser?.withdrawGroup,
          branch: selectedUser?.adminBranch?.map((branch: any) => branch.branch),
        }}
      >
        <div className="flex justify-between flex-wrap border-t-[1px] border-gray-100 mt-5 pt-5">
          <div className="flex flex-col w-full md:w-1/2 border-0 md:border-e-[1px] border-gray-100 pe-5 box-border mb-5 md:mb-0">
            <div className="flex flex-col w-full">
              <Label text="Full name" />
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please input user full name!",
                  },
                ]}
              >
                <CustomInput placeholder="Enter user full name" />
              </Form.Item>
            </div>
            <div className="flex flex-col w-full">
              <Label text="Email" />
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: "Please input your email!",
                  },
                  {
                    type: "email",
                    message: "Please enter valid email!",
                  },
                ]}
              >
                <CustomInput
                  disabled={edit}
                  placeholder="Enter your email address"
                />
              </Form.Item>
            </div>
            <div className="flex flex-col w-full">
              <Label text="Password" />
              <Form.Item
                name="password"
                rules={[
                  {
                    required: selectedUser?.id ? false : true,
                    message: "Please input your password!",
                  },
                ]}
              >
                <Input.Password
                  className="input-field-container"
                  type="password"
                  placeholder="Enter your password"
                  onChange={onPasswordChange}
                  autoComplete="new-password"
                />
              </Form.Item>
              {form.isFieldTouched("password") && (
                <span
                  className={clsx(
                    "flex items-center",
                    !passwordRoleOne ? "text-red-500" : "text-green-100"
                  )}
                >
                  {passwordRoleOne ? (
                    <img className=" me-1" src={CorrectImage} alt="" />
                  ) : (
                    <CloseOutlined className="me-1" />
                  )}{" "}
                  contains at least 8 characters
                </span>
              )}
              {form.isFieldTouched("password") && (
                <span
                  className={clsx(
                    "flex items-center",
                    !passwordRoleTwo ? "text-red-500" : "text-green-100"
                  )}
                >
                  {passwordRoleTwo ? (
                    <img className="me-1" src={CorrectImage} alt="" />
                  ) : (
                    <CloseOutlined className="me-1" />
                  )}{" "}
                  contain both lower & upper case letters
                </span>
              )}
              {form.isFieldTouched("password") && (
                <span
                  className={clsx(
                    "flex items-center",
                    !passwordRoleThree ? "text-red-500" : "text-green-100"
                  )}
                >
                  {passwordRoleThree ? (
                    <img className="me-1" src={CorrectImage} alt="" />
                  ) : (
                    <CloseOutlined className="me-1" />
                  )}{" "}
                  contains at least one number & a special character
                </span>
              )}
            </div>
            <div className="flex flex-col w-full mt-5">
              <Label text="Branch" />
              <Form.Item
                name="branch"
                rules={[
                  {
                    required: true,
                    message: "Please select the branch!",
                  },
                ]}
              >
                <Select mode="multiple" placeholder="Select Branch">
                  <Option value={"KW"}>
                    <span>KUWAIT</span>
                  </Option>
                  <Option value={"SA"}>
                    <span>Saudi Arabia</span>
                  </Option>
                  <Option value={"AE"}>
                    <span>Uniter Arab Emirates</span>
                  </Option>
                </Select>
              </Form.Item>
            </div>
            <div className="flex flex-col w-full mt-5">
              <Label text="Status" />
              <Form.Item name="isActive">
                <div className="switch flex items-center">
                  <span className=" me-1">Active </span>
                  <Switch
                    // disabled
                    checked={isActive}
                    onChange={(checked: boolean) => setIsActive(checked)}
                  />{" "}
                  <span className="ms-1">Deactive</span>
                </div>
              </Form.Item>
            </div>
            {!updateProfile && (
              <div className="w-full flex flex-col box-border">
                <Divider />
                <div className="flex flex-col w-full mb-2">
                  <Label text="KYC approvals" />
                </div>
                <div className="flex w-full border-[1px] border-lightGray-400 box-border p-3 rounded-xl bg-white h-[170px] overflow-auto">
                  <Form.Item name="kycReviewGroup">
                    <Radio.Group defaultValue={selectedUser?.kycReviewGroup}>
                      <Space direction="vertical">
                        <Radio value={null}>None</Radio>
                        <Radio value={"A"}>WM support</Radio>
                        <Radio value={"B"}>Compliance</Radio>
                        <Radio value={"C"}>WM control</Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </div>
              </div>
            )}
          </div>
          {!updateProfile && (
            <div className="w-full md:w-1/2 flex flex-col box-border ps-0 md:ps-5">
              <div className="flex flex-col w-full">
                <Label text="Roles" />
                <Form.Item>
                  <CustomInput
                    style={{ borderRadius: "25px" }}
                    prefix={<SearchOutlined style={{ color: "#bcb8b8" }} />}
                    placeholder="Search role / root"
                    onChange={filterRole}
                  />
                </Form.Item>
              </div>
              <div className="flex w-full border-[1px] border-lightGray-400 box-border p-3 rounded-xl bg-white h-[361px] overflow-auto mb-5">
                <Form.Item
                  name="roles"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one role!",
                    },
                  ]}
                >
                  <CheckboxGroup options={searchedRole} />
                </Form.Item>
              </div>
              <div className="w-full flex flex-col box-border">
                <Divider />
                <div className="flex flex-col w-full mb-2">
                  <Label text="Withdrawal groups" />
                </div>
                <div className="flex w-full border-[1px] border-lightGray-400 box-border p-3 rounded-xl bg-white h-[200px] overflow-auto">
                  <Form.Item name="withdrawGroup">
                    <Radio.Group defaultValue={selectedUser?.withdrawGroup}>
                      <Space direction="vertical">
                        <Radio value={null}>None</Radio>
                        <Radio value={"AMD"}>Asset management</Radio>
                        <Radio value={"WMD"}>Wealth management</Radio>
                        <Radio value={"OPS"}>Operations</Radio>
                        <Radio value={"CEO"}>CEO</Radio>
                      </Space>
                    </Radio.Group>
                  </Form.Item>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end w-full mt-9">
          <div className="flex justify-between items-center w-full md:w-1/2">
            <button
              type="button"
              onClick={onCancel}
              className="border-[1px] rounded-xl py-3 border-blue-500 text-blue-500 w-[49%] font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="border-[1px] rounded-xl border-blue-500 bg-blue-500 text-white w-[49%] py-3 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              disabled={
                !submitLoginForm ||
                (!edit &&
                  (!passwordRoleOne || !passwordRoleTwo || !passwordRoleThree))
              }
            >
              Confirm
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default AddEditUser;
